<template>
  <div class="tool-page">
    <div class="tool-header">
      <h1>{{ $t('calculator.title') }}</h1>
      <p>{{ $t('calculator.subtitle') }}</p>
    </div>

    <div class="calculator-card">
      <div class="display">
        <div class="expression">{{ expression || '0' }}</div>
        <div class="result">{{ displayResult }}</div>
      </div>

      <div class="mode-switch">
        <button 
          :class="['mode-btn', { active: mode === 'basic' }]" 
          @click="mode = 'basic'"
        >{{ $t('calculator.basic') }}</button>
        <button 
          :class="['mode-btn', { active: mode === 'scientific' }]" 
          @click="mode = 'scientific'"
        >{{ $t('calculator.scientific') }}</button>
      </div>

      <div class="buttons" :class="{ scientific: mode === 'scientific' }">
        <!-- Scientific buttons (only shown in scientific mode) -->
        <template v-if="mode === 'scientific'">
          <button class="btn func" @click="appendFunc('sin(')">sin</button>
          <button class="btn func" @click="appendFunc('cos(')">cos</button>
          <button class="btn func" @click="appendFunc('tan(')">tan</button>
          <button class="btn func" @click="appendFunc('log(')">log</button>
          <button class="btn func" @click="appendFunc('ln(')">ln</button>
          <button class="btn func" @click="appendFunc('sqrt(')">√</button>
          <button class="btn func" @click="append('^')">x^y</button>
          <button class="btn func" @click="append('Math.PI')">π</button>
          <button class="btn func" @click="append('Math.E')">e</button>
          <button class="btn func" @click="append('(')">(</button>
          <button class="btn func" @click="append(')')">)</button>
          <button class="btn func" @click="appendFunc('abs(')">|x|</button>
        </template>

        <!-- Basic buttons -->
        <button class="btn clear" @click="clear">AC</button>
        <button class="btn op" @click="backspace">⌫</button>
        <button class="btn op" @click="append('%')">%</button>
        <button class="btn op" @click="append('/')">÷</button>

        <button class="btn num" @click="append('7')">7</button>
        <button class="btn num" @click="append('8')">8</button>
        <button class="btn num" @click="append('9')">9</button>
        <button class="btn op" @click="append('*')">×</button>

        <button class="btn num" @click="append('4')">4</button>
        <button class="btn num" @click="append('5')">5</button>
        <button class="btn num" @click="append('6')">6</button>
        <button class="btn op" @click="append('-')">−</button>

        <button class="btn num" @click="append('1')">1</button>
        <button class="btn num" @click="append('2')">2</button>
        <button class="btn num" @click="append('3')">3</button>
        <button class="btn op" @click="append('+')">+</button>

        <button class="btn num" @click="toggleSign">±</button>
        <button class="btn num" @click="append('0')">0</button>
        <button class="btn num" @click="append('.')">.</button>
        <button class="btn equal" @click="calculate">=</button>
      </div>

      <div class="history" v-if="history.length > 0">
        <div class="history-header">
          <span>{{ $t('calculator.history') }}</span>
          <button class="btn-link" @click="clearHistory">{{ $t('calculator.clearHistory') }}</button>
        </div>
        <div class="history-list">
          <div 
            class="history-item" 
            v-for="(item, index) in history" 
            :key="index"
            @click="useHistory(item)"
          >
            <span class="hist-expr">{{ item.expression }}</span>
            <span class="hist-result">= {{ item.result }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 工具说明 -->
    <div class="tool-description">
      <h2>{{ $t('calculator.whatIsCalculator') }}</h2>
      <p>{{ $t('calculator.whatIsCalculatorDesc') }}</p>

      <h2>{{ $t('calculator.basicFunctions') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('calculator.basicFunctionsItems')" :key="'basic'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('calculator.scientificFunctions') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('calculator.scientificFunctionsItems')" :key="'sci'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('calculator.calculatorTips') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('calculator.calculatorTipsItems')" :key="'tip'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('calculator.calculatorHistory') }}</h2>
      <p>{{ $t('calculator.calculatorHistoryDesc') }}</p>

      <h2>{{ $t('calculator.operationPriority') }}</h2>
      <p>{{ $t('calculator.operationPriorityDesc') }}</p>
      <ul>
        <li v-for="(item, index) in $t('calculator.operationPriorityItems')" :key="'pri'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('calculator.dailyCalculation') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('calculator.dailyCalculationItems')" :key="'daily'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('calculator.keyboardShortcuts') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('calculator.keyboardShortcutsItems')" :key="'key'+index">{{ item }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CalculatorTool',
  data() {
    return {
      expression: '',
      result: null,
      mode: 'basic',
      history: []
    }
  },
  computed: {
    displayResult() {
      if (this.result === null) return ''
      if (this.result === 'Error') return 'Error'
      return '= ' + this.formatNumber(this.result)
    }
  },
  methods: {
    append(value) {
      this.expression += value
      this.result = null
    },

    appendFunc(func) {
      this.expression += func
      this.result = null
    },

    clear() {
      this.expression = ''
      this.result = null
    },

    backspace() {
      this.expression = this.expression.slice(0, -1)
      this.result = null
    },

    toggleSign() {
      if (this.expression === '') {
        this.expression = '-'
      } else if (this.expression.startsWith('-')) {
        this.expression = this.expression.slice(1)
      } else {
        this.expression = '-' + this.expression
      }
    },

    calculate() {
      if (!this.expression) return

      try {
        // Replace display operators with JS operators
        let evalExpr = this.expression
          .replace(/×/g, '*')
          .replace(/÷/g, '/')
          .replace(/−/g, '-')
          .replace(/\^/g, '**')
          .replace(/sin\(/g, 'Math.sin(')
          .replace(/cos\(/g, 'Math.cos(')
          .replace(/tan\(/g, 'Math.tan(')
          .replace(/log\(/g, 'Math.log10(')
          .replace(/ln\(/g, 'Math.log(')
          .replace(/sqrt\(/g, 'Math.sqrt(')
          .replace(/abs\(/g, 'Math.abs(')

        // Safe evaluation
        // eslint-disable-next-line no-new-func
        const result = new Function('return ' + evalExpr)()
        
        if (typeof result === 'number' && !isNaN(result) && isFinite(result)) {
          this.result = result
          this.addToHistory(this.expression, result)
        } else {
          this.result = 'Error'
        }
      } catch (e) {
        this.result = 'Error'
      }
    },

    formatNumber(num) {
      if (Number.isInteger(num)) {
        return num.toLocaleString()
      }
      // Round to 10 decimal places to avoid floating point issues
      const rounded = Math.round(num * 1e10) / 1e10
      return rounded.toLocaleString(undefined, { maximumFractionDigits: 10 })
    },

    addToHistory(expression, result) {
      this.history.unshift({
        expression,
        result: this.formatNumber(result)
      })
      if (this.history.length > 10) {
        this.history.pop()
      }
    },

    clearHistory() {
      this.history = []
    },

    useHistory(item) {
      this.expression = item.expression
      this.result = null
    }
  }
}
</script>

<style scoped>
.tool-page {
  max-width: 420px;
  margin: 0 auto;
}

.tool-header {
  text-align: center;
  margin-bottom: 32px;
}

.tool-header h1 {
  font-size: 32px;
  margin-bottom: 12px;
  color: var(--text-primary);
}

.tool-header p {
  color: var(--text-secondary);
  font-size: 16px;
}

.calculator-card {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 24px;
  padding: 24px;
  box-shadow: var(--shadow-lg);
  transition: background 0.3s, border-color 0.3s;
}

.display {
  background: var(--bg-input);
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  min-height: 100px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  text-align: right;
  border: 1px solid var(--border-color);
  transition: background 0.3s, border-color 0.3s;
}

.expression {
  font-size: 18px;
  color: var(--text-secondary);
  word-break: break-all;
  min-height: 24px;
}

.result {
  font-size: 36px;
  font-weight: 600;
  color: var(--text-primary);
  margin-top: 8px;
  font-family: 'Monaco', 'Menlo', monospace;
}

.mode-switch {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.mode-btn {
  flex: 1;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 10px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
}

.mode-btn.active {
  background: var(--accent-color);
  border-color: var(--accent-color);
  color: white;
}

.buttons {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
}

.buttons.scientific {
  grid-template-columns: repeat(4, 1fr);
}

.btn {
  aspect-ratio: 1;
  border: none;
  border-radius: 12px;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn.num {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
}

.btn.num:hover {
  background: var(--bg-hover);
}

.btn.op {
  background: var(--bg-hover);
  color: var(--warning-color);
  border: 1px solid var(--border-color);
}

.btn.op:hover {
  background: var(--bg-tertiary);
}

.btn.func {
  background: var(--accent-light);
  color: var(--accent-color);
  border: 1px solid var(--border-color);
  font-size: 14px;
  aspect-ratio: auto;
  padding: 12px 8px;
}

.btn.func:hover {
  background: var(--accent-color);
  color: white;
}

.btn.clear {
  background: var(--error-color);
  color: white;
  border: none;
}

.btn.clear:hover {
  opacity: 0.9;
}

.btn.equal {
  background: var(--accent-color);
  color: white;
  border: none;
}

.btn.equal:hover {
  background: var(--accent-hover);
}

.btn:active {
  transform: scale(0.95);
}

.history {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid var(--border-color);
}

.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-size: 14px;
  color: var(--text-secondary);
}

.btn-link {
  background: none;
  border: none;
  color: var(--accent-color);
  cursor: pointer;
  font-size: 13px;
}

.btn-link:hover {
  text-decoration: underline;
}

.history-list {
  max-height: 200px;
  overflow-y: auto;
}

.history-item {
  display: flex;
  justify-content: space-between;
  padding: 8px 12px;
  background: var(--bg-tertiary);
  border-radius: 8px;
  margin-bottom: 6px;
  cursor: pointer;
  transition: background 0.2s;
}

.history-item:hover {
  background: var(--bg-hover);
}

.hist-expr {
  color: var(--text-secondary);
  font-size: 14px;
}

.hist-result {
  color: var(--accent-color);
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 14px;
}

.tool-description {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 32px;
  margin-top: 32px;
  box-shadow: var(--shadow-md);
}

.tool-description h2 {
  font-size: 20px;
  color: var(--text-primary);
  margin: 28px 0 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-color);
}

.tool-description h2:first-child {
  margin-top: 0;
}

.tool-description p {
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.8;
  margin-bottom: 12px;
}

.tool-description ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.tool-description li {
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.8;
  padding-left: 20px;
  position: relative;
  margin-bottom: 8px;
}

.tool-description li::before {
  content: '•';
  color: var(--accent-color);
  position: absolute;
  left: 0;
}

.tool-page {
  max-width: 600px;
}
</style>
